import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FaStore,
  FaUserTie,
  FaBuilding,
  FaQrcode,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaCheckCircle,
} from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import { getSystemDashboardChartsStats, getSystemDashboardStats } from "../api/api";
import "../styles/Dashboard.css";

Chart.register(...registerables);

/* ─── Static label sets ─────────────────────────────────────── */
const LABELS = {
  week:  ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
  month: ["Week 1","Week 2","Week 3","Week 4"],
  year:  ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
};

/* ─── Helpers ────────────────────────────────────────────────── */
const sum  = (arr) => arr.reduce((a, b) => a + b, 0);
const peak = (arr) => Math.max(...arr);
const fmt  = (n)   => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

function buildData(apiData, filter) {
  return LABELS[filter].map((label) => {
    const match = apiData?.find?.((d) => d.label === label);
    return { label, count: match?.count ?? 0, amount: match?.amount ?? 0 };
  });
}

function Kpi({ label, value, icon, accent, trend, progress }) {
  return (
    <div className="msd-kpi">
      <div className="msd-kpi__head">
        <div className="msd-kpi__label">{label}</div>
        <div className="msd-kpi__icon" style={{ color: accent, background: accent + "22" }}>
          {icon}
        </div>
      </div>
      <div className="msd-kpi__value">{value}</div>

      {typeof trend === "string" && trend.length > 0 ? (
        <div className="msd-kpi__trend">{trend}</div>
      ) : null}

      {progress ? (
        <div className="msd-kpi__progress">
          <div className="msd-kpi__progress-bar" style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: accent }} />
        </div>
      ) : (
        <div className="msd-kpi__spark" style={{ background: `linear-gradient(90deg, ${accent} 0%, transparent 80%)` }} />
      )}
    </div>
  );
}

/* ─── Chart factory helpers ──────────────────────────────────── */
const PURPLE  = "#534AB7";
const TEAL    = "#1D9E75";
const GRID    = "rgba(0,0,0,0.06)";
const TICK    = "#9ca3af";

const tooltipDefaults = {
  backgroundColor: "#1e1b4b",
  titleColor: "#fff",
  bodyColor: "#c4b5fd",
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
};

function lineConfig(labels, values, color, prefix = "") {
  return {
    type: "line",
    data: {
      labels,
      datasets: [{
        data: values,
        borderColor: color,
        borderWidth: 2.5,
        pointBackgroundColor: color,
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        backgroundColor: (ctx) => {
          const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 180);
          g.addColorStop(0, color + "40");
          g.addColorStop(1, color + "00");
          return g;
        },
        tension: 0.4,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 550, easing: "easeInOutQuart" },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltipDefaults,
          callbacks: {
            label: (ctx) => `  ${prefix}${Number(ctx.parsed.y).toLocaleString()}`,
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: TICK, font: { size: 11 } } },
        y: {
          grid: { color: GRID, drawBorder: false },
          ticks: {
            color: TICK, font: { size: 11 },
            callback: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          },
        },
      },
    },
  };
}

function barConfig(labels, values) {
  const maxVal = Math.max(...values);
  const colors = values.map((v) =>
    v === maxVal ? PURPLE : v > maxVal * 0.65 ? "#7F77DD" : "#AFA9EC"
  );
  return {
    type: "bar",
    data: {
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.6,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 550, easing: "easeInOutQuart" },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltipDefaults,
          callbacks: { label: (ctx) => `  Count: ${ctx.parsed.y.toLocaleString()}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: TICK, font: { size: 11 } } },
        y: {
          grid: { color: GRID, drawBorder: false },
          ticks: {
            color: TICK, font: { size: 11 },
            callback: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          },
        },
      },
    },
  };
}

function doughnutConfig(active, inactive) {
  return {
    type: "doughnut",
    data: {
      labels: ["Active", "Inactive"],
      datasets: [{
        data: [active, inactive],
        backgroundColor: [PURPLE, "#EEEDFE"],
        borderColor: [PURPLE, "#AFA9EC"],
        borderWidth: 1.5,
        hoverOffset: 8,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      animation: { duration: 700, animateRotate: true },
      plugins: {
        legend: { display: false },
        tooltip: { ...tooltipDefaults },
      },
    },
  };
}

/* ─── Reusable chart canvas wrapper ──────────────────────────── */
function ChartCanvas({ id, config, height = 200 }) {
  const ref = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !config) return;
    if (instanceRef.current) instanceRef.current.destroy();
    instanceRef.current = new Chart(ref.current, config);
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [config]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <canvas ref={ref} id={id} />
    </div>
  );
}

/* ─── Stat mini-tile ─────────────────────────────────────────── */
function MiniStat({ label, value }) {
  return (
    <div style={{
      flex: 1, background: "#f9fafb", borderRadius: 10,
      padding: "10px 14px", border: "0.5px solid #e5e7eb",
    }}>
      <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</p>
      <p style={{ margin: "3px 0 0", fontSize: 20, fontWeight: 700, color: "#1e1b4b" }}>{value}</p>
    </div>
  );
}

/* ─── Legend pill ────────────────────────────────────────────── */
function Legend({ items }) {
  return (
    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
      {items.map((item) => (
        <span key={item.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
          <span style={{
            width: 10, height: 10, borderRadius: 2,
            background: item.color,
            border: item.border ? `0.5px solid ${item.border}` : undefined,
          }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ─── Top stat tile ──────────────────────────────────────────── */
function Tile({ label, value, icon, accent }) {
  return (
    <div style={{
      position: "relative", background: "#fff", borderRadius: 16,
      padding: "1.2rem 1.3rem 1rem", overflow: "hidden", cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
      transition: "transform .22s, box-shadow .22s",
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)";
      }}
    >
      {/* left accent bar */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
        background: accent, borderRadius: "16px 0 0 16px",
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: accent + "22", color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.15rem",
        }}>
          {icon}
        </div>
        <p style={{ margin: 0, fontSize: "0.78rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em" }}>
          {label}
        </p>
      </div>
      <p style={{ margin: 0, fontSize: "2rem", fontWeight: 700, color: "#1e1b4b", lineHeight: 1 }}>
        {Number(value).toLocaleString()}
      </p>
    </div>
  );
}

/* ─── Chart card wrapper ─────────────────────────────────────── */
function ChartCard({ title, children, action }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: "1.4rem 1.4rem 1.2rem",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
      transition: "box-shadow .2s",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#1e1b4b" }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ─── Main dashboard ─────────────────────────────────────────── */
const AdminDashboard = ({ isSidebarOpen }) => {
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("week");
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const [search, setSearch] = useState("");

  /* fetch summary tiles */
  useEffect(() => {
    getSystemDashboardStats()
      .then((res) => { if (res?.data) setStats(res.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* fetch chart data whenever filter changes */
  const fetchCharts = useCallback(async (f) => {
    setChartLoading(true);
    try {
      const res = await getSystemDashboardChartsStats(f);
      setChartData(buildData(res?.data, f));
    } catch {
      setChartData(LABELS[f].map((label) => ({ label, count: 0, amount: 0 })));
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => { fetchCharts(filter); }, [filter, fetchCharts]);

  /* derived stats */
  const counts  = chartData.map((d) => d.count);
  const amounts = chartData.map((d) => d.amount);
  const labels  = chartData.map((d) => d.label);

  const totalCount  = sum(counts);
  const peakCount   = counts.length ? peak(counts) : 0;
  const totalAmount = sum(amounts);
  const avgAmount   = amounts.length ? Math.round(totalAmount / amounts.length) : 0;

  const dynamicQrShare = stats?.dynamicQrShare ?? 64;
  const staticQrShare = stats?.staticQrShare ?? 36;
  const successRate = stats?.successRate ?? 99.8;

  /* chart configs (memoised on data changes) */
  const countLineConfig  = chartData.length ? lineConfig(labels, counts, PURPLE) : null;
  const amountLineConfig = chartData.length ? lineConfig(labels, amounts, TEAL, "Rs. ") : null;
  const barChartConfig   = chartData.length ? barConfig(labels, counts) : null;

  const activeOrgs   = stats?.activeOrganizations ?? 0;
  const inactiveOrgs = (stats?.totalOrganizations ?? 0) - activeOrgs;
  const doughnutConf = doughnutConfig(activeOrgs, inactiveOrgs);

  /* filter pill */
  const FilterBar = () => (
    <div style={{ display: "flex", gap: 6 }}>
      {["week","month","year"].map((f) => (
        <button
          key={f}
          onClick={() => setFilter(f)}
          style={{
            padding: "5px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
            border: filter === f ? "none" : "0.5px solid #e5e7eb",
            background: filter === f ? PURPLE : "#f9fafb",
            color: filter === f ? "#fff" : "#6b7280",
            fontWeight: filter === f ? 600 : 400,
            transition: "all .18s",
          }}
        >
          {f === "week" ? "This week" : f === "month" ? "This month" : "This year"}
        </button>
      ))}
    </div>
  );

  const tiles = [
    {
      label: "Total Orgs",
      value: Number(stats?.totalOrganizations ?? 0).toLocaleString(),
      icon: <FaBuilding />,
      accent: "#534AB7",
      trend: "↑ 12%",
    },
    {
      label: "Merchants",
      value: fmt(stats?.activeMerchants ?? 0),
      icon: <FaStore />,
      accent: "#1D9E75",
      trend: "↑ 8%",
    },
    {
      label: "Transactions",
      value: fmt(totalCount),
      icon: <FaExchangeAlt />,
      accent: "#1976D2",
      trend: "↗",
    },
    {
      label: "Revenue",
      value: totalAmount ? `Rs. ${fmt(totalAmount)}` : "Rs. 0",
      icon: <FaMoneyBillWave />,
      accent: "#D85A30",
      trend: "⚡",
    },
    {
      label: "Dynamic QR",
      value: `${dynamicQrShare}%`,
      icon: <FaQrcode />,
      accent: "#22c55e",
      progress: dynamicQrShare,
    },
    {
      label: "Static QR",
      value: `${staticQrShare}%`,
      icon: <FaQrcode />,
      accent: "#f59e0b",
      progress: staticQrShare,
    },
    {
      label: "Success Rate",
      value: `${successRate}%`,
      icon: <FaCheckCircle />,
      accent: "#06b6d4",
      progress: Math.max(0, Math.min(100, Number(successRate))),
    },
  ];

  const activities = [
    { title: "New Organization Boarded", desc: "Global Logistics Inc. added 42 terminals.", when: "2 mins ago" },
    { title: "Security Audit Passed", desc: "Quarterly PCI DSS compliance verified.", when: "45 mins ago" },
    { title: "API Latency Spike", desc: "Detected in Region: Asia-Southwest.", when: "1 hour ago" },
    { title: "New Admin Registered", desc: "A new admin has been created.", when: "3 hours ago" },
  ];

  const topOrganizations = [
    { name: "Velocity Retail", volume: "Rs 245,000", success: "99.9%", status: "ACTIVE" },
    { name: "Aether Logistics", volume: "Rs 182,400", success: "99.7%", status: "ACTIVE" },
    { name: "Zenith Hotels", volume: "Rs 121,000", success: "94.2%", status: "REVIEW" },
    { name: "Nova Finance", volume: "Rs 98,300", success: "98.8%", status: "ACTIVE" },
  ];

  /* ── Render ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="msd-kpis">
        {tiles.map((t, i) => (
          <Kpi
            key={i}
            label={t.label}
            value={t.value}
            icon={t.icon}
            accent={t.accent}
            trend={t.trend}
            progress={t.progress}
          />
        ))}
      </div>

      <div className="msd-maingrid">
        <div className="msd-card msd-card--wide">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Transaction Trends</div>
              <div className="msd-card__subtitle">Volume and value analysis across orgs</div>
            </div>
            <div className="msd-card__actions">
              <FilterBar />
            </div>
          </div>

          <div className="msd-card__body">
            {chartLoading
              ? <p className="msd-muted">Loading…</p>
              : <ChartCanvas id="msd-trends" config={countLineConfig} height={240} />
            }
          </div>
        </div>

        <div className="msd-card">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">QR Adoption</div>
              <div className="msd-card__subtitle">Method distribution</div>
            </div>
          </div>
          <div className="msd-card__body msd-donut">
            <div className="msd-donut__chart">
              <ChartCanvas id="msd-qr" config={doughnutConf} height={180} />
            </div>
            <div className="msd-donut__legend">
              <div className="msd-legend-row"><span className="msd-dot" style={{ background: PURPLE }} />Dynamic QR<span className="msd-legend-val">58%</span></div>
              <div className="msd-legend-row"><span className="msd-dot" style={{ background: TEAL }} />Static QR<span className="msd-legend-val">24%</span></div>
              <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#64748b" }} />Manual Pay<span className="msd-legend-val">18%</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="msd-bottomgrid">
        <div className="msd-card">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Recent Activities</div>
              <div className="msd-card__subtitle">System events and onboarding updates</div>
            </div>
          </div>
          <div className="msd-activity">
            {activities.map((a) => (
              <div key={a.title} className="msd-activity__row">
                <div className="msd-activity__meta">
                  <div className="msd-activity__title">{a.title}</div>
                  <div className="msd-activity__desc">{a.desc}</div>
                </div>
                <div className="msd-activity__when">{a.when}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="msd-card msd-card--wide">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Top Organizations</div>
              <div className="msd-card__subtitle">Daily volume and success rate</div>
            </div>
          </div>
          <div className="msd-table">
            <div className="msd-table__head">
              <div>Organization</div>
              <div>Daily Volume</div>
              <div>Success Rate</div>
              <div>Status</div>
            </div>
            {topOrganizations.map((o) => (
              <div key={o.name} className="msd-table__row">
                <div className="msd-org">
                  <div className="msd-org__avatar">{o.name.slice(0, 1)}</div>
                  <div className="msd-org__name">{o.name}</div>
                </div>
                <div className="msd-mono">{o.volume}</div>
                <div className="msd-mono">{o.success}</div>
                <div>
                  <span className={o.status === "ACTIVE" ? "msd-badge msd-badge--active" : "msd-badge msd-badge--review"}>
                    {o.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
